import { lazy, useEffect } from "react";
import { Navigate, useNavigate, useRoutes } from "react-router-dom";

// layouts
import DashboardLayout from "./layouts/dashboard";
import SimpleLayout from "./layouts/simple";

//
import { verifyTokenExpiry } from "./utils/verifyToken";

import { DeliveryRequestProvider } from "./pages/deliver_request/context/deliveryRequestContext";

import Page404 from "./pages/404/Page404";
import LoginPage from "./pages/login/LoginPage";

const ProductTabPage = lazy(() => import("./pages/product/productTab"));
const PamojaPage = lazy(() => import("./pages/pamoja/PamojaPage"));
const DeliverRequestPage = lazy(() => import("./pages/deliver_request/delivery_request"));
const JipangeConfigurationPage = lazy(() => import("./pages/jipange"));
const ResetPasswordPage = lazy(() => import("./pages/reset_password/ResetPasswordPage"));
const PromoNotificationPage = lazy(() => import("./pages/notification/PromoNotificationPage"));

//Users
const UsersPage = lazy(() => import("./pages/users/index"));
const UsersList = lazy(() => import("./pages/users/list/userList"));
const UserDetails = lazy(() => import("./pages/users/details/userDetails"));

//Partners
const PartnerTabPage = lazy(() => import("./pages/partners/partnerTab"));
const PartnersPage = lazy(() => import("./pages/partners/index"));
const AddPartner = lazy(() => import("./pages/partners/add/addPartner"));
const EditPartner = lazy(() => import("./pages/partners/edit/editPartner"));

//Brands
const BrandsPage = lazy(() => import("./pages/brands/index"));
const BrandsList = lazy(() => import("./pages/brands/list/brandList"));
const EditBrand = lazy(() => import("./pages/brands/edit/editBrand"));
const AddBrand = lazy(() => import("./pages/brands/add/addBrand"));

//Products
const ProductPage = lazy(() => import("./pages/product/index"));
const AddProduct = lazy(() => import("./pages/product/add/addProduct"));
const EditProduct = lazy(() => import("./pages/product/edit/editProduct"));
const AddVariant = lazy(() => import("./pages/product/variant/addVariant"));

//Orders
const OrdersPage = lazy(() => import("./pages/orders/index"));
const OrdersList = lazy(() => import("./pages/orders/list/orderList"));
const OrderDetailsPage = lazy(() => import("./pages/orders/details/orderDetails"));

//Promos
const PromosPage = lazy(() => import("./pages/promos/index"));
const PromoList = lazy(() => import("./pages/promos/list/promoList"));
const EditPromo = lazy(() => import("./pages/promos/edit/editPromo"));
const AddPromo = lazy(() => import("./pages/promos/add/addPromo"));

//Audit
const AuditPage = lazy(() => import("./pages/audit/index"));
const AuditList = lazy(() => import("./pages/audit/list/auditList"));

//Message Center
const MessageCenterPage = lazy(() => import("./pages/message_center/index"));
const MessageList = lazy(() => import("./pages/message_center/list/messageList"));
const MessageDetails = lazy(() => import("./pages/message_center/details/messageDetails"));

// AppConfig
const AppConfig = lazy(() => import("./pages/app_config/index"));
const WebConfig = lazy(() => import("./pages/web_config/index"));
const SlideshowConfiguration = lazy(() => import("./pages/app_config/bannerConfig/index"));
const WebConfigurations = lazy(() => import("./pages/web_config/config/index"));
const EditBannerConfiguration = lazy(() => import("./pages/app_config/bannerConfig/edit/index"));
const EditWebBannerConfiguration = lazy(() => import("./pages/web_config/edit/index"));
const ManageBannerImages = lazy(() => import("./pages/app_config/bannerConfig/manageImages/index"));
const ManageConfigurationImages = lazy(() => import("./pages/web_config/manage_images/index"));
const ForceUpdateVersions = lazy(() => import("./pages/app_config/forceUpdateVersion/index"));
const LatestVersion = lazy(() => import("./pages/app_config/latestVersion/index"));
const RecommendedMinimumVersion = lazy(
  () => import("./pages/app_config/recommendedMinimumVersion/index")
);
const FreeDeliveryThreshold = lazy(() => import("./pages/app_config/freeDeliveryThreshold/index"));

//Reports
const ReportPage = lazy(() => import("./pages/reports/index"));
const ReportsDashbaord = lazy(() => import("./pages//reports/reportsPage"));

// ----------------------------------------------------------------------

export default function Router() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Check token status
  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
    } else {
      verifyTokenExpiry(token, navigate);
    }
  }, [token, navigate]);

  const routes = useRoutes([
    {
      path: "/dashboard",
      element: <DashboardLayout />,
      children: [
        //Products
        { index: true, element: <Navigate to="products" replace /> },
        {
          path: "products",
          element: <ProductPage />,
          children: [
            { index: true, element: <ProductTabPage /> },
            { path: "add", element: <AddProduct /> },
            { path: "variant/add", element: <AddVariant /> },
            { path: "edit/:productVariantId", element: <EditProduct /> }
          ]
        },

        //App configurations
        { path: "/dashboard/app-config", element: <AppConfig /> },
        { path: "/dashboard/web-config", element: <WebConfig /> },
        { path: "/dashboard/web-config/:config", element: <WebConfigurations /> },
        {
          path: "/dashboard/firebase-config/:config/manage-images",
          element: <ManageConfigurationImages />
        },
        { path: "/dashboard/app-config/bannerConfig", element: <SlideshowConfiguration /> },
        {
          path: "/dashboard/app-config/bannerConfig/manage-images",
          element: <ManageBannerImages />
        },
        {
          path: "/dashboard/app-config/forceUpdateVersions",
          element: <ForceUpdateVersions />
        },
        {
          path: "/dashboard/app-config/latestVersion",
          element: <LatestVersion />
        },
        {
          path: "/dashboard/app-config/recommendedMinimumVersion",
          element: <RecommendedMinimumVersion />
        },
        { path: "/dashboard/app-config/freeDeliveryThreshold", element: <FreeDeliveryThreshold /> },
        {
          path: "/dashboard/app-config/bannerConfig/edit/:imageName",
          element: <EditBannerConfiguration />
        },
        {
          path: "/dashboard/firebase-config/:config/edit/:imageName",
          element: <EditWebBannerConfiguration />
        },

        //Bramds
        {
          path: "/dashboard/brands",
          element: <BrandsPage />,
          children: [
            { path: "", element: <BrandsList /> },
            { path: "edit/:brandId", element: <EditBrand /> },
            { path: "add", element: <AddBrand /> }
          ]
        },

        //Users
        {
          path: "/dashboard/users",
          element: <UsersPage />,
          children: [
            { path: "", element: <UsersList /> },
            { path: "details/:userId", element: <UserDetails /> }
          ]
        },

        //Orders
        {
          path: "/dashboard/orders",
          element: <OrdersPage />,
          children: [
            { path: "", element: <OrdersList /> },
            { path: "details/:orderId", element: <OrderDetailsPage /> }
          ]
        },
        { path: "", element: <OrdersPage /> },
        {
          path: "orders/delivery-request",
          element: (
            <DeliveryRequestProvider>
              <DeliverRequestPage />
            </DeliveryRequestProvider>
          )
        },

        //Promos
        {
          path: "/dashboard/promos",
          element: <PromosPage />,
          children: [
            { path: "", element: <PromoList /> },
            { path: "edit/:promoCodeId", element: <EditPromo /> },
            { path: "add", element: <AddPromo /> }
          ]
        },

        //Partners
        {
          path: "/dashboard/partners",
          element: <PartnersPage />,
          children: [
            { index: true, element: <PartnerTabPage /> },
            { path: "edit/:partnerId", element: <EditPartner /> },
            { path: "add", element: <AddPartner /> }
          ]
        },

        //Message Center
        {
          path: "/dashboard/message-center",
          element: <MessageCenterPage />,
          children: [
            { path: "", element: <MessageList /> },
            { path: "details/:messageId", element: <MessageDetails /> }
          ]
        },

        //Reports
        {
          path: "/dashboard/reports",
          element: <ReportPage />,
          children: [{ path: "", element: <ReportsDashbaord /> }]
        }
      ]
    },
    {
      path: "/dashboard/okoa",
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard/products" />, index: true },
        { path: "pamoja", element: <PamojaPage /> },
        { path: "jipange", element: <JipangeConfigurationPage /> }
      ]
    },
    {
      path: "/dashboard/audit",
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard/settings" />, index: true },
        //Audit
        {
          path: "/dashboard/audit/logs",
          element: <AuditPage />,
          children: [{ path: "", element: <AuditList /> }]
        },
        { path: "notifications", element: <PromoNotificationPage /> }
      ]
    },

    //Login
    {
      path: "login",
      element: <LoginPage />
    },

    //Rdset Password
    {
      path: "reset-password",
      element: <ResetPasswordPage />
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/products" />, index: true },
        { path: "404", element: <Page404 /> },
        { path: "*", element: <Navigate to="/404" /> }
      ]
    },

    //Redirect all unmatched routes to 404
    {
      path: "*",
      element: <Navigate to="/404" replace />
    }
  ]);

  return routes;
}
